import { useCallback } from 'react';
import {
  Button,
  EnrollMfa,
  EnrollMfaDescription,
  EnrollMfaError,
  EnrollMfaFlow,
  EnrollMfaFlowBackButton,
  EnrollMfaFooter,
  EnrollMfaImage,
  EnrollMfaModal,
  EnrollMfaModalContent,
  EnrollMfaProps,
  EnrollMfaTitle,
  IconXMark,
  useAppConfig,
} from '@coinbase/cdp-react';
import { useUser } from 'apps/cdp-web-demo/app/(main)/UserContext';
import { clsx } from 'clsx/lite';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';

import { Dialog, DialogClose } from '../Dialog/Dialog';

import styles from './AddMfaDialog.module.css';

type AddMfaProps = Omit<EnrollMfaProps, 'children'>;

const VIEW = 'add-mfa';

export function AddMfaDialog({ onEnrollSuccess, className = '', ...props }: AddMfaProps) {
  const { showCoinbaseFooter } = useAppConfig();
  const { loggedInView, setLoggedInView } = useUser();
  const { isTablet, isDesktop } = useBreakpoints();
  const isWideLayout = isTablet || isDesktop;
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setLoggedInView(null);
      } else {
        setLoggedInView(VIEW);
      }
    },
    [setLoggedInView],
  );

  const closeButton = (
    <DialogClose asChild>
      <Button
        className={styles['close-button']}
        aria-label="Close"
        size="md"
        variant="transparentSecondary"
      >
        <IconXMark className={styles['close-icon']} />
      </Button>
    </DialogClose>
  );

  return (
    <>
      {isWideLayout && (
        <Dialog
          open={loggedInView === VIEW}
          onOpenChange={handleOpenChange}
          title="Add multi-factor authentication"
          className={styles['add-mfa-dialog']}
        >
          <EnrollMfa
            className={clsx(
              className,
              styles.enroll,
              showCoinbaseFooter ? '' : styles['no-footer'],
            )}
            onEnrollSuccess={onEnrollSuccess}
            {...props}
          >
            {(state) => (
              <>
                <div
                  className={clsx(
                    styles['flex-container'],
                    state.step === 'list' ? styles['gap-3'] : '',
                  )}
                >
                  {state.step === 'list' ? (
                    <>
                      <div>
                        <div className={styles.header}>
                          <EnrollMfaTitle step="list" method={state.method} />
                          {closeButton}
                        </div>
                        <EnrollMfaDescription step="list" method={state.method} />
                      </div>
                      <EnrollMfaError />
                    </>
                  ) : (
                    <div className={styles.header}>
                      <EnrollMfaFlowBackButton step={state.step} />
                      {closeButton}
                    </div>
                  )}
                  <EnrollMfaFlow>
                    {({ step, method, Content }) => (
                      <div className={`${styles['flex-container']} ${styles['gap-2']}`}>
                        {step !== 'list' && (
                          <>
                            <EnrollMfaImage
                              className={`${styles.img} ${styles.centered}`}
                              step={step}
                              method={method}
                            />
                            <div className={styles.centered}>
                              <EnrollMfaTitle step={step} method={method} />
                              <EnrollMfaDescription step={step} method={method} />
                            </div>
                          </>
                        )}
                        {Content}
                      </div>
                    )}
                  </EnrollMfaFlow>
                </div>
                {showCoinbaseFooter && <EnrollMfaFooter className={styles.footer} />}
              </>
            )}
          </EnrollMfa>
        </Dialog>
      )}
      {!isWideLayout && (
        <EnrollMfaModal
          open={loggedInView === VIEW}
          setIsOpen={handleOpenChange}
          onEnrollSuccess={onEnrollSuccess}
          {...props}
        >
          <EnrollMfaModalContent />
        </EnrollMfaModal>
      )}
    </>
  );
}
